// Remises de virements fournisseurs (B1b, 03/11/2026). La banque débite en une seule
// ligne de relevé un lot de virements ; le fichier CSV de la remise en donne le détail.
// Contrôles bloquants : somme des virements = total de la remise = ligne de débit du
// relevé. Chaque virement : facture d'achat (« certaine » cochée d'office ; « à vérifier »
// jamais), facture de l'exercice antérieur (4718, à confirmer ligne par ligne) ou compte
// d'attente 471 (signalé). Rien n'est enregistré avant la confirmation.
import { useEffect, useState } from "react";
import api from "../services/api";
import { downloadProtectedFile } from "../utils/openBlob";
import InvoicePdfButton from "./InvoicePdfButton";

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = reject;
  r.readAsDataURL(file);
});
const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const frDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const STATUS = {
  certaine: { label: "certaine", color: "#16a34a" },
  a_verifier: { label: "à vérifier", color: "#b45309" },
  a_confirmer: { label: "à confirmer", color: "#b45309" },
  signale: { label: "sans facture", color: "#6b7280" },
};
const MODE = {
  ventilation: "ligne en attente : une écriture de banque répartit le débit entre les fournisseurs",
  reclassement: "ligne déjà validée sur le compte d'attente 471 : une écriture la reporte sur les fournisseurs, les virements sans facture restent sur 471",
};
const choiceValue = (c) => (!c ? "" : c.kind === "facture" ? `f${c.purchase_invoice_id}` : c.kind);
const parseChoice = (v) => (!v ? null : v.startsWith("f") ? { kind: "facture", purchase_invoice_id: Number(v.slice(1)) } : { kind: v });

export default function BankRemisesModal({ onClose, onDone }) {
  const [payload, setPayload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [choices, setChoices] = useState({}); // clé de remise → { n° de ligne → choix }
  const [chosen, setChosen] = useState({});   // clé de remise → importer ?
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const choose = async (file) => {
    if (!file) return;
    setBusy(true); setError(""); setPreview(null); setResults(null);
    try {
      const data = { filename: file.name, content_base64: await toBase64(file) };
      const p = await api.bankImport.remisesPreview(data);
      setPayload(data); setPreview(p);
      setChoices(Object.fromEntries(p.remises.map(r => [r.key, Object.fromEntries(r.transfers.map(t => [t.line_no, t.choice]))])));
      setChosen(Object.fromEntries(p.remises.filter(r => r.ok).map(r => [r.key, true])));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const missing = (r) => r.transfers.filter(t => !choices[r.key]?.[t.line_no]).length;
  const selected = (preview?.remises || []).filter(r => r.ok && chosen[r.key]);
  const blockedByChoice = selected.some(r => missing(r) > 0);

  const confirm = async () => {
    if (!window.confirm(`Importer ${selected.length} remise(s) ? Les écritures sont passées à la date de chaque ligne du relevé ; chaque remise est traitée avec ses contrôles, une remise refusée n'empêche pas les autres.`)) return;
    setBusy(true); setError("");
    try {
      const r = await api.bankImport.remisesConfirm({ ...payload, choices: Object.fromEntries(selected.map(x => [x.key, choices[x.key]])) });
      setResults(r);
      onDone && onDone(r);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 1150, width: "96%" }}>
        <div className="modal-header"><h3 className="modal-title">🧾 Remises de virements fournisseurs</h3><button className="modal-close" onClick={onClose}>✕</button></div>
        <div style={{ fontSize: 12.5, color: "#374151", marginBottom: 10 }}>
          Une <strong>remise</strong> est un lot de virements que la banque débite en une seule ligne du relevé. Choisissez le fichier CSV de la remise (export de la banque, ou le{" "}
          <button className="btn sm" onClick={() => downloadProtectedFile(api.bankImport.remisesTemplate)}>⬇ modèle CSV</button>) : chaque virement est relié à sa facture d'achat.
          La somme des virements doit être égale au total de la remise et à la ligne de débit du relevé, au centime. <strong>Rien n'est enregistré avant la confirmation.</strong>
        </div>
        <input type="file" accept=".csv,.txt" disabled={busy} onChange={e => choose(e.target.files?.[0])} />
        {busy && <span style={{ marginLeft: 8, fontSize: 12 }}>Lecture…</span>}
        {error && <div className="alert error" style={{ marginTop: 8 }}>{error}</div>}

        {preview && !results && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12.5, marginBottom: 8 }}>
              {preview.ok} remise(s) juste(s){preview.blocked ? `, ${preview.blocked} bloquée(s) (motif sous chacune)` : ""}.
              {" "}<strong>Exercice antérieur</strong> : virement qui règle une facture datée d'avant le début de l'exercice, déjà dans les comptes de l'exercice précédent ; porté sur le compte {preview.prior_account.code}
              {preview.prior_account.create ? " (créé dans votre plan à la confirmation)" : ""}, « {preview.prior_account.label} », jusqu'à la reprise des soldes d'ouverture (à-nouveaux).
              {" "}<strong>Compte d'attente 471</strong> : virement sans facture dans l'application, signalé jusqu'à ce que sa facture soit importée.
            </div>
            {preview.remises.map(r => (
              <div key={r.key} style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 10, marginBottom: 10, background: r.ok ? "#fff" : "#fef2f2" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  {r.ok && <input type="checkbox" checked={!!chosen[r.key]} onChange={e => setChosen(s => ({ ...s, [r.key]: e.target.checked }))} />}
                  <strong>Remise {r.ref || ""} du {frDate(r.exec_date)}</strong>
                  <span>{r.salary ? "remise de salaires (détail non affiché)" : `${r.transfers.length} virement(s) · total ${eur(r.total)}`}</span>
                  {r.statement && <span style={{ fontSize: 12, color: "#374151" }}>ligne du relevé du {frDate(r.statement.date)} ({eur(r.statement.amount)})</span>}
                  {r.ok ? <span style={{ color: "#16a34a", fontSize: 12 }}>✓ somme = total = relevé</span> : <span style={{ color: "#b91c1c", fontSize: 12 }}>⛔ bloquée</span>}
                </div>
                {!r.ok && <ul style={{ margin: "6px 0 0 18px", color: "#b91c1c", fontSize: 12.5 }}>{r.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>}
                {r.ok && <div style={{ fontSize: 12, color: "#6b7280", margin: "4px 0" }}>{MODE[r.statement.mode]}</div>}
                {r.ok && (
                  <table className="table" style={{ fontSize: 12.5, marginTop: 6 }}>
                    <thead><tr><th>Bénéficiaire</th><th>Référence</th><th style={{ textAlign: "right" }}>Montant</th><th>Proposition</th><th>Imputation</th></tr></thead>
                    <tbody>
                      {r.transfers.map(t => {
                        const value = choiceValue(choices[r.key]?.[t.line_no]);
                        const opt = t.options.find(o => `f${o.purchase_invoice_id}` === value);
                        return (
                          <tr key={t.line_no} style={!value ? { background: "#fffbeb" } : undefined}>
                            <td>{t.beneficiary}</td>
                            <td>{t.reference || "—"}</td>
                            <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>{eur(t.amount)}</td>
                            <td><span style={{ color: STATUS[t.proposal.status]?.color, fontWeight: 600 }}>{STATUS[t.proposal.status]?.label}</span> <span style={{ color: "#6b7280" }}>— {t.proposal.reason}</span></td>
                            <td>
                              <select value={value} onChange={e => setChoices(s => ({ ...s, [r.key]: { ...s[r.key], [t.line_no]: parseChoice(e.target.value) } }))} style={{ maxWidth: 330 }}>
                                {!value && <option value="">— à choisir —</option>}
                                {t.options.map(o => (
                                  <option key={o.purchase_invoice_id} value={`f${o.purchase_invoice_id}`}>
                                    Facture {o.supplier_ref || o.number} · {o.tiers} · {frDate(o.date)} · reste {eur(o.remaining)}{o.after_transfer ? " · datée après le virement" : ""}{o.exact ? "" : " · paiement partiel"}
                                  </option>
                                ))}
                                <option value="anterieur">Facture de l'exercice antérieur ({preview.prior_account.code})</option>
                                <option value="attente">Sans facture : compte d'attente 471</option>
                              </select>
                              {opt && <InvoicePdfButton kind="achat" id={opt.purchase_invoice_id} />}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                {r.ok && missing(r) > 0 && <div style={{ fontSize: 12, color: "#b45309" }}>{missing(r)} virement(s) à choisir (ligne surlignée) avant la confirmation.</div>}
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn" onClick={onClose}>Fermer</button>
              <button className="btn primary" disabled={busy || !selected.length || blockedByChoice} onClick={confirm}>✓ Importer {selected.length} remise(s)</button>
            </div>
          </div>
        )}

        {results && (
          <div style={{ marginTop: 12 }}>
            {results.results.map(x => (
              <div key={x.key} style={{ fontSize: 13, marginBottom: 4, color: x.ok ? "#16a34a" : "#b91c1c" }}>
                {x.ok ? `✅ Remise ${x.key.includes("|") ? "" : x.key} importée : ${x.invoices} facture(s) payée(s)${x.prior ? `, ${eur(x.prior)} sur ${x.prior_account}${x.prior_account_created ? " (compte créé)" : ""}` : ""}${x.waiting ? `, ${eur(x.waiting)} sur 471 (signalés)` : ""}.`
                  : `⛔ Remise ${x.key.includes("|") ? "" : x.key} refusée : ${x.error}`}
              </div>
            ))}
            <div style={{ textAlign: "right" }}><button className="btn" onClick={onClose}>Fermer</button></div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Détail de la remise d'une ligne du relevé. */
export function BankRemiseDetail({ row, onClose }) {
  const [data, setData] = useState(undefined);
  const [error, setError] = useState("");
  useEffect(() => { api.bankImport.remiseOf(row.id).then(d => setData(d)).catch(e => setError(e.message)); }, [row.id]);
  const TREAT = { facture: "facture d'achat", anterieur: "exercice antérieur", attente: "compte d'attente 471" };
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 900, width: "96%" }}>
        <div className="modal-header"><h3 className="modal-title">🧾 Remise du {frDate(data?.exec_date || row.date)}</h3><button className="modal-close" onClick={onClose}>✕</button></div>
        {error && <div className="alert error">{error}</div>}
        {data === undefined && !error && <div>Chargement…</div>}
        {data === null && <div>Aucune remise active sur cette ligne.</div>}
        {data && (
          <>
            <div style={{ fontSize: 12.5, marginBottom: 6 }}>{data.transfer_count} virement(s) · total {eur(data.total)} · {data.mode === "ventilation" ? "ventilation de la ligne" : "reclassement depuis 471"}. Pour défaire la remise : {data.mode === "ventilation" ? "« Annuler la validation »" : "« Annuler le rattachement »"} sur la ligne.</div>
            <table className="table" style={{ fontSize: 12.5 }}>
              <thead><tr><th>Bénéficiaire</th><th>Référence</th><th style={{ textAlign: "right" }}>Montant</th><th>Imputation</th></tr></thead>
              <tbody>
                {data.transfers.map(t => (
                  <tr key={t.line_no}><td>{t.beneficiary}</td><td>{t.reference || "—"}</td><td style={{ textAlign: "right" }}>{eur(t.amount)}</td>
                    <td>{t.treatment === "facture" ? `Facture ${t.invoice_number}` : `${TREAT[t.treatment]} (${t.account_code})`}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
